// @flow
import React from 'react';
import defaultMapStyles from '@justpark/helpers/src/mapStylesToStatic/defaultMapStyles';
import { loadGoogle } from '../../../helpers/maps';
import GoogleMapContext from './GoogleMapContext';
import type { Google } from '../types';

declare var google: Google;

type Props = {
  center?: {
    lat: number,
    lng: number
  },
  zoomControl?: boolean,
  mapTypeControl?: 'none' | 'standard',
  zoom?: number,
  maxZoom?: number,
  mapTypeId?: 'roadmap' | 'satellite',
  children?: any,
  scrollwheel?: true,
  disableDefaultUI?: boolean
};

type State = {
  init: boolean
};

const getMapTypeControls = (type, disableDefaultUI) => {
  if (type === 'standard' && !disableDefaultUI) {
    return {
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        mapTypeIds: [
          google.maps.MapTypeId.ROADMAP,
          google.maps.MapTypeId.SATELLITE
        ]
      }
    };
  }

  return {
    mapTypeControl: false
  };
};

/**
 * Class based component that loads the google maps SDK and creates a new Google Map
 * When ready it renders it's children inside a context provider so we know that all children will have access to google JS
 */
export default class GoogleMapLoader extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      init: false
    };
  }

  componentDidMount() {
    this.mounted = true;

    loadGoogle().then(() => {
      if (this.mounted) {
        this.initMap();
      }
    });
  }

  componentDidUpdate(prevProps: GoogleMapLoaderProps) {
    const { zoomControl } = this.props;

    if (zoomControl !== prevProps.zoomControl) {
      this.gMap.setOptions({ zoomControl });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  initMap() {
    const mapElement = this.map;
    const {
      center,
      zoom,
      maxZoom,
      mapTypeId,
      scrollwheel,
      zoomControl,
      disableDefaultUI,
      mapTypeControl
    } = this.props;

    this.gMap = new google.maps.Map(mapElement, {
      center,
      zoom,
      ...getMapTypeControls(mapTypeControl, disableDefaultUI),
      disableDefaultUI,
      clickableIcons: false,
      streetViewControl: false,
      gestureHandling: 'greedy',
      fullscreenControl: false,
      zoomControl,
      maxZoom,
      scrollwheel,
      minZoom: 6,
      styles: defaultMapStyles,
      mapTypeId,
      tilt: 0,
      rotateControl: false,
      isFractionalZoomEnabled: true
    });

    if (window.Cypress) {
      google.maps.event.addListenerOnce(this.gMap, 'tilesloaded', () => {
        this.gMap.hasLoaded = true;
      });

      window.CypressGoogleMap = this.gMap;
    }

    this.setState({
      init: true
    });
  }

  render() {
    const { init } = this.state;
    const { children } = this.props;

    return (
      <div
        className="map"
        ref={(div) => {
          this.map = div;
        }}
      >
        {init && (
          <GoogleMapContext.Provider value={this.gMap}>
            {children}
          </GoogleMapContext.Provider>
        )}
      </div>
    );
  }
}

GoogleMapLoader.defaultProps = {
  zoom: 14,
  maxZoom: 18,
  mapTypeId: 'roadmap',
  mapTypeControl: 'standard',
  scrollwheel: true,
  disableDefaultUI: false
};
